<script>
/*
 * Same convention as the sibling WhatsappReports.vue: this report screen is
 * PT-only by product decision, so bare-string rules are scoped off here rather
 * than half-translating the file.
 */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
/* global axios */
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const fmtNum = n => (n == null ? '0' : Number(n).toLocaleString('pt-BR'));
const fmtPct = n => `${Number(n || 0).toFixed(1)}%`;
const fmtMoney = (n, cur = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: cur }).format(n || 0);

const fmtDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtDuration = seconds => {
  if (!seconds || seconds <= 0) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const AUDIENCE_LABELS = {
  Label: 'Etiqueta do contato',
  ConversationLabel: 'Etiqueta da conversa',
  KanbanStage: 'Etapa do Kanban',
  PhoneNumber: 'Importação CSV',
};

const STATUS_LABELS = {
  active: 'Ativa',
  completed: 'Concluída',
};

export default {
  name: 'WhatsappCampaigns',

  components: { Line },

  props: {
    inboxId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      error: null,
      campaigns: [],

      detailLoading: false,
      detailError: null,
      selectedCampaignId: null,
      detail: null,

      detailTab: 'recipients',
      recipientStatusFilter: 'all',
      recipientSearch: '',
      recipientPage: 1,
      recipientsPerPage: 20,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    view() {
      return this.selectedCampaignId ? 'detail' : 'list';
    },

    totalsByStatus() {
      return this.campaigns.reduce(
        (acc, c) => {
          acc[c.status] = (acc[c.status] || 0) + 1;
          return acc;
        },
        {}
      );
    },

    // Sales attributed to this campaign. Absent for accounts that never turned
    // revenue tracking on, so every field needs a zero default.
    detailRevenue() {
      return (
        this.detail?.revenue || {
          total: 0,
          conversions_count: 0,
          avg_ticket: 0,
          conversion_rate: 0,
          revenue_per_recipient: 0,
          currency: 'BRL',
          roi_percent: null,
        }
      );
    },

    detailRoiLabel() {
      const roi = this.detailRevenue.roi_percent;
      if (roi === null || roi === undefined) return '—';
      return `${roi >= 0 ? '+' : ''}${Number(roi).toFixed(0)}%`;
    },

    detailRoiClass() {
      const roi = this.detailRevenue.roi_percent;
      if (roi === null || roi === undefined) return 'text-n-slate-12';
      return roi >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
    },

    filteredRecipients() {
      if (!this.detail) return [];
      const q = this.recipientSearch.trim().toLowerCase();
      return this.detail.recipients.filter(r => {
        if (this.recipientStatusFilter !== 'all') {
          const ds = r.delivery_status || (r.status === 'failed' ? 'failed' : 'sent');
          if (ds !== this.recipientStatusFilter) return false;
        }
        if (q) {
          const name = (r.contact_name || '').toLowerCase();
          const phone = (r.contact_phone || '').toLowerCase();
          return name.includes(q) || phone.includes(q);
        }
        return true;
      });
    },

    // O servidor manda só os primeiros envios (recipients_limit). A busca e o
    // filtro rodam sobre eles; numa campanha maior a tela precisa dizer isso.
    recipientsTruncated() {
      if (!this.detail) return false;
      const total = Number(this.detail.recipients_total) || 0;
      return total > (this.detail.recipients || []).length;
    },

    campaignReportsRoute() {
      return {
        name: 'campaign_reports',
        params: { accountId: this.accountId },
        query: { inbox: String(this.inboxId) },
      };
    },

    totalRecipientPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredRecipients.length / this.recipientsPerPage)
      );
    },

    paginatedRecipients() {
      const start = (this.recipientPage - 1) * this.recipientsPerPage;
      return this.filteredRecipients.slice(start, start + this.recipientsPerPage);
    },

    errorEntries() {
      if (!this.detail) return [];
      return Object.entries(this.detail.error_breakdown || {})
        .map(([code, count]) => ({ code, count }))
        .sort((a, b) => b.count - a.count);
    },

    dailyChartData() {
      if (!this.detail) return { labels: [], datasets: [] };
      const stats = this.detail.daily_stats || [];
      const labels = stats.map(s => {
        const [, m, d] = s.date.split('-');
        return `${d}/${m}`;
      });
      return {
        labels,
        datasets: [
          {
            label: 'Enviadas',
            data: stats.map(s => s.sent || 0),
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.12)',
            tension: 0.3,
            fill: true,
          },
          {
            label: 'Entregues',
            data: stats.map(s => s.delivered || 0),
            borderColor: '#14b8a6',
            tension: 0.3,
            fill: false,
          },
          {
            label: 'Lidas',
            data: stats.map(s => s.read || 0),
            borderColor: '#f59e0b',
            tension: 0.3,
            fill: false,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
        },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { stepSize: 1 } },
        },
      };
    },
  },

  watch: {
    recipientStatusFilter() {
      this.recipientPage = 1;
    },
    recipientSearch() {
      this.recipientPage = 1;
    },
  },

  mounted() {
    this.fetchList();
  },

  methods: {
    fmtNum,
    fmtPct,
    fmtMoney,
    fmtDateTime,
    fmtDuration,

    audienceLabel(type) {
      return AUDIENCE_LABELS[type] || type || '—';
    },

    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },

    async fetchList() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/whatsapp_campaign_metrics`
        );
        this.campaigns = data || [];
      } catch (err) {
        this.error = 'Erro ao carregar campanhas. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },

    async openDetail(campaignId) {
      this.selectedCampaignId = campaignId;
      this.detailLoading = true;
      this.detailError = null;
      this.detail = null;
      this.detailTab = 'recipients';
      this.recipientStatusFilter = 'all';
      this.recipientSearch = '';
      this.recipientPage = 1;

      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/whatsapp_campaign_metrics/${campaignId}`
        );
        this.detail = data;
      } catch (err) {
        this.detailError = 'Erro ao carregar detalhes da campanha.';
      } finally {
        this.detailLoading = false;
      }
    },

    closeDetail() {
      this.selectedCampaignId = null;
      this.detail = null;
    },

    recipientStatusClass(recipient) {
      const ds = recipient.delivery_status;
      if (recipient.status === 'failed' || ds === 'failed') {
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      }
      if (ds === 'read') {
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      }
      if (ds === 'delivered') {
        return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
      }
      return 'bg-n-slate-3 text-n-slate-11 dark:bg-n-solid-3';
    },

    recipientStatusLabel(recipient) {
      const ds = recipient.delivery_status;
      if (recipient.status === 'failed' || ds === 'failed') return 'Falhou';
      if (ds === 'read') return 'Lida';
      if (ds === 'delivered') return 'Entregue';
      return 'Enviada';
    },

    deliveryRateColor(rate) {
      if (rate >= 95) return 'text-green-600 dark:text-green-400';
      if (rate >= 85) return 'text-amber-600 dark:text-amber-400';
      return 'text-red-600 dark:text-red-400';
    },
  },
};
</script>

<template>
  <div class="p-5 text-n-slate-12 text-sm flex flex-col gap-4">

    <!-- ══════════════════ LIST VIEW ══════════════════ -->
    <template v-if="view === 'list'">
      <div class="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 class="text-base font-bold text-n-slate-12 m-0">Campanhas WhatsApp</h2>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ fmtNum(campaigns.length) }} campanha{{ campaigns.length !== 1 ? 's' : '' }}
            <span v-if="totalsByStatus.active"> · {{ totalsByStatus.active }} ativas</span>
            <span v-if="totalsByStatus.completed"> · {{ totalsByStatus.completed }} concluídas</span>
          </p>
        </div>
      </div>

      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
      >
        <span class="i-ri-loader-4-line size-6 animate-spin" />
        <span>Carregando campanhas...</span>
      </div>

      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
      >
        <span class="i-ri-wifi-off-line size-6" />
        <span>{{ error }}</span>
        <button
          class="px-3 py-1.5 rounded-xl text-xs font-medium bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 dark:bg-n-solid-3 dark:hover:bg-n-solid-2"
          @click="fetchList"
        >
          Tentar novamente
        </button>
      </div>

      <div
        v-else-if="!campaigns.length"
        class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
      >
        <span class="i-ri-megaphone-line size-8" />
        <p>Nenhuma campanha encontrada para este inbox.</p>
      </div>

      <div v-else class="grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        <button
          v-for="c in campaigns"
          :key="c.id"
          class="text-left p-4 rounded-2xl border border-n-weak bg-n-surface-1 hover:border-n-blue-9 transition-colors flex flex-col gap-3"
          @click="openDetail(c.id)"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-bold text-n-slate-12 truncate m-0">{{ c.title }}</h3>
              <p class="text-xs text-n-slate-11 mt-0.5 truncate">
                {{ audienceLabel(c.audience_type) }}
              </p>
            </div>
            <span
              class="px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold flex-shrink-0"
              :class="c.status === 'completed'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'"
            >
              {{ statusLabel(c.status) }}
            </span>
          </div>

          <div class="grid grid-cols-3 gap-2">
            <div class="flex flex-col">
              <span class="text-[0.625rem] text-n-slate-11 uppercase tracking-wide">Audiência</span>
              <span class="text-sm font-bold text-n-slate-12">{{ fmtNum(c.total_audience) }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-[0.625rem] text-n-slate-11 uppercase tracking-wide">Enviadas</span>
              <span class="text-sm font-bold text-n-slate-12">{{ fmtNum(c.total_sent) }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-[0.625rem] text-n-slate-11 uppercase tracking-wide">Lidas</span>
              <span class="text-sm font-bold" :class="deliveryRateColor(c.read_rate)">
                {{ fmtPct(c.read_rate) }}
              </span>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between text-[0.6875rem] text-n-slate-11">
              <span>Entregues</span>
              <span>{{ fmtPct(c.delivery_rate) }}</span>
            </div>
            <div class="h-1.5 bg-n-slate-3 dark:bg-n-solid-3 rounded-full overflow-hidden">
              <div
                class="h-full bg-n-blue-9 rounded-full transition-all"
                :style="{ width: `${Math.min(100, c.delivery_rate)}%` }"
              />
            </div>
          </div>

          <div class="flex items-center justify-between text-xs pt-1 border-t border-n-weak">
            <span class="text-n-slate-11 flex items-center gap-1">
              <span class="i-ri-chat-follow-up-line size-3.5" />
              {{ fmtNum(c.total_replied) }} respostas
            </span>
            <span class="text-n-slate-12 font-semibold">
              {{ fmtMoney(c.estimated_cost, c.pricing_currency) }}
            </span>
          </div>

          <div
            v-if="c.revenue_total > 0"
            class="flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400"
          >
            <span class="flex items-center gap-1">
              <span class="i-ri-arrow-right-up-long-line size-3.5" />
              {{ fmtNum(c.revenue_conversions) }} vendas
            </span>
            <span class="font-semibold">
              {{ fmtMoney(c.revenue_total, c.pricing_currency) }}
            </span>
          </div>

          <div v-if="!c.has_metric" class="text-[0.6875rem] text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <span class="i-ri-time-line size-3" />
            Aguardando primeiro status da Meta
          </div>
        </button>
      </div>
    </template>

    <!-- ══════════════════ DETAIL VIEW ══════════════════ -->
    <template v-else>
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div class="flex items-start gap-3 min-w-0 flex-1">
          <button
            class="size-8 flex items-center justify-center rounded-xl text-n-slate-11 hover:bg-n-slate-3 dark:hover:bg-n-solid-3 flex-shrink-0"
            title="Voltar"
            @click="closeDetail"
          >
            <span class="i-ri-arrow-left-line size-5" />
          </button>
          <div class="min-w-0">
            <h2 v-if="detail" class="text-base font-bold text-n-slate-12 m-0 truncate">
              {{ detail.campaign.title }}
            </h2>
            <h2 v-else class="text-base font-bold text-n-slate-12 m-0">Carregando...</h2>
            <p v-if="detail" class="text-xs text-n-slate-11 mt-0.5">
              {{ audienceLabel(detail.campaign.audience_type) }} ·
              {{ fmtDateTime(detail.campaign.scheduled_at) }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="detailLoading"
        class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
      >
        <span class="i-ri-loader-4-line size-6 animate-spin" />
        <span>Carregando métricas...</span>
      </div>

      <div
        v-else-if="detailError"
        class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
      >
        <span class="i-ri-wifi-off-line size-6" />
        <span>{{ detailError }}</span>
      </div>

      <template v-else-if="detail">
        <!-- Funnel -->
        <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-5">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Funil de conversão</h3>
          <div class="flex flex-col gap-2">
            <div
              v-for="stage in detail.funnel"
              :key="stage.stage"
              class="flex items-center gap-3"
            >
              <span class="w-24 text-xs text-n-slate-11 flex-shrink-0">{{ stage.label }}</span>
              <div class="flex-1 h-7 bg-n-slate-3 dark:bg-n-solid-3 rounded relative overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded transition-all"
                  :style="{ width: `${Math.min(100, stage.percent)}%` }"
                />
                <div class="absolute inset-0 flex items-center px-3 text-xs font-semibold text-n-slate-12">
                  {{ fmtNum(stage.count) }} ({{ fmtPct(stage.percent) }})
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Revenue attributed to this campaign -->
        <div
          v-if="detailRevenue.conversions_count > 0"
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-5 flex flex-col gap-3"
        >
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <h3 class="text-sm font-semibold text-n-slate-12 m-0">Retorno</h3>
            <span
              v-if="detail.utm_campaign"
              class="text-[0.6875rem] text-n-slate-11"
            >
              utm_campaign:
              <code class="text-n-slate-12">{{ detail.utm_campaign }}</code>
            </span>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="flex flex-col gap-1">
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Receita</span
              >
              <span class="text-xl font-extrabold text-n-slate-12">
                {{ fmtMoney(detailRevenue.total, detailRevenue.currency) }}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Vendas</span
              >
              <span class="text-xl font-extrabold text-n-slate-12">
                {{ fmtNum(detailRevenue.conversions_count) }}
              </span>
              <span class="text-[0.6875rem] text-n-slate-11">
                {{ fmtPct(detailRevenue.conversion_rate) }} dos envios
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Ticket médio</span
              >
              <span class="text-xl font-extrabold text-n-slate-12">
                {{ fmtMoney(detailRevenue.avg_ticket, detailRevenue.currency) }}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >ROI</span
              >
              <span class="text-xl font-extrabold" :class="detailRoiClass">
                {{ detailRoiLabel }}
              </span>
              <span class="text-[0.6875rem] text-n-slate-11">
                {{
                  fmtMoney(
                    detailRevenue.revenue_per_recipient,
                    detailRevenue.currency
                  )
                }}
                por envio
              </span>
            </div>
          </div>
        </div>

        <!-- KPI grid -->
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Audiência</span>
            <span class="text-xl font-extrabold text-n-slate-12">{{ fmtNum(detail.metrics.total_audience) }}</span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtPct(detail.metrics.coverage_rate) }} cobertura</span>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Enviadas</span>
            <span class="text-xl font-extrabold text-n-slate-12">{{ fmtNum(detail.metrics.total_sent) }}</span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtNum(detail.metrics.pending_count) }} pendentes</span>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Entregues</span>
            <span class="text-xl font-medium" :class="deliveryRateColor(detail.metrics.delivery_rate)">
              {{ fmtPct(detail.metrics.delivery_rate) }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtNum(detail.metrics.total_delivered) }} msgs</span>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Lidas</span>
            <span class="text-xl font-medium text-n-slate-12">{{ fmtPct(detail.metrics.read_rate) }}</span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtNum(detail.metrics.total_read) }} leituras</span>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Respondidas</span>
            <span class="text-xl font-extrabold text-n-slate-12">{{ fmtNum(detail.metrics.total_replied) }}</span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtPct(detail.metrics.reply_rate) }} reply rate</span>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex flex-col gap-1">
            <span class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Custo estimado</span>
            <span class="text-xl font-extrabold text-n-slate-12">
              {{ fmtMoney(detail.cost.estimated_cost, detail.cost.currency) }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">{{ fmtPct(detail.metrics.failure_rate) }} falha</span>
          </div>
        </div>

        <!-- Secondary indicators -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex items-center gap-3">
            <span class="i-ri-time-line size-5 text-n-slate-11" />
            <div>
              <div class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Tempo médio 1ª resposta</div>
              <div class="text-sm font-bold text-n-slate-12">
                {{ fmtDuration(detail.metrics.avg_time_to_reply_seconds) }}
              </div>
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex items-center gap-3">
            <span class="i-ri-user-unfollow-line size-5 text-n-slate-11" />
            <div>
              <div class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Drop-off (lido sem resposta)</div>
              <div class="text-sm font-bold text-n-slate-12">{{ fmtNum(detail.metrics.drop_off_count) }}</div>
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-3 flex items-center gap-3">
            <span class="i-ri-timer-line size-5 text-n-slate-11" />
            <div>
              <div class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide">Primeiro envio</div>
              <div class="text-sm font-bold text-n-slate-12">
                {{ fmtDateTime(detail.metrics.first_sent_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex gap-1 border-b border-n-weak">
          <button
            v-for="tab in [
              { id: 'recipients', label: 'Destinatários', icon: 'i-ri-group-line' },
              { id: 'errors', label: 'Erros', icon: 'i-ri-alert-line' },
              { id: 'timeline', label: 'Linha do tempo', icon: 'i-ri-pulse-line' },
              { id: 'cost', label: 'Custo', icon: 'i-ri-cash-line' },
            ]"
            :key="tab.id"
            class="px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors -mb-px"
            :class="detailTab === tab.id
              ? 'border-n-blue-9 text-n-blue-9'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
            @click="detailTab = tab.id"
          >
            <span :class="tab.icon" class="size-3.5" />
            {{ tab.label }}
          </button>
        </div>

        <!-- Recipients tab -->
        <div v-if="detailTab === 'recipients'" class="flex flex-col gap-3">
          <div
            v-if="recipientsTruncated"
            class="flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl border border-n-amber-6 text-sm text-n-slate-12"
            role="status"
            data-testid="recipients-truncated"
          >
            <span class="i-ri-information-line size-4 text-n-amber-11" />
            <span>
              {{
                $t('CAMPAIGN.WHATSAPP_REPORT.RECIPIENTS_TRUNCATED', {
                  shown: fmtNum(detail.recipients.length),
                  total: fmtNum(detail.recipients_total),
                })
              }}
            </span>
            <router-link
              :to="campaignReportsRoute"
              class="underline text-n-blue-11 underline-offset-2"
            >
              {{ $t('CAMPAIGN.WHATSAPP_REPORT.OPEN_FULL_REPORT') }}
            </router-link>
          </div>
          <div class="flex gap-2 flex-wrap items-center">
            <div class="relative flex-1 min-w-[200px]">
              <span class="i-ri-search-line size-4 absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10 pointer-events-none" />
              <input
                v-model="recipientSearch"
                type="text"
                placeholder="Buscar por nome ou telefone..."
                class="w-full pl-9 pr-3 py-1.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
              />
            </div>
            <select
              v-model="recipientStatusFilter"
              class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
            >
              <option value="all">Todos status</option>
              <option value="sent">Enviadas</option>
              <option value="delivered">Entregues</option>
              <option value="read">Lidas</option>
              <option value="failed">Falhadas</option>
            </select>
          </div>

          <div v-if="paginatedRecipients.length" class="rounded-2xl border border-n-weak overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-slate-2 dark:bg-n-solid-2">
                <tr>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Contato</th>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Telefone</th>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Status</th>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Enviado</th>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Entregue</th>
                  <th class="text-left text-[0.6875rem] font-semibold text-n-slate-11 uppercase tracking-wide px-3 py-2.5">Lido</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="r in paginatedRecipients"
                  :key="r.message_id"
                  class="border-t border-n-weak hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
                >
                  <td class="px-3 py-2.5 text-n-slate-12 font-medium">{{ r.contact_name || '—' }}</td>
                  <td class="px-3 py-2.5 text-n-slate-11 font-mono text-xs">{{ r.contact_phone || '—' }}</td>
                  <td class="px-3 py-2.5">
                    <span
                      class="inline-block px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold"
                      :class="recipientStatusClass(r)"
                    >
                      {{ recipientStatusLabel(r) }}
                    </span>
                  </td>
                  <td class="px-3 py-2.5 text-n-slate-11 text-xs">{{ fmtDateTime(r.sent_at) }}</td>
                  <td class="px-3 py-2.5 text-n-slate-11 text-xs">{{ fmtDateTime(r.delivered_at) }}</td>
                  <td class="px-3 py-2.5 text-n-slate-11 text-xs">{{ fmtDateTime(r.read_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="flex flex-col items-center justify-center gap-2 py-12 text-n-slate-11">
            <span class="i-ri-group-line size-6" />
            <span>Nenhum destinatário corresponde aos filtros.</span>
          </div>

          <div
            v-if="totalRecipientPages > 1"
            class="flex items-center justify-between text-xs text-n-slate-11"
          >
            <span>
              Página {{ recipientPage }} de {{ totalRecipientPages }}
              · {{ fmtNum(filteredRecipients.length) }} destinatários
            </span>
            <div class="flex gap-1">
              <button
                class="px-2 py-1 rounded border border-n-weak text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-solid-3 disabled:opacity-50"
                :disabled="recipientPage === 1"
                @click="recipientPage--"
              >
                Anterior
              </button>
              <button
                class="px-2 py-1 rounded border border-n-weak text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-solid-3 disabled:opacity-50"
                :disabled="recipientPage >= totalRecipientPages"
                @click="recipientPage++"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>

        <!-- Errors tab -->
        <div v-if="detailTab === 'errors'" class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Distribuição de falhas</h3>
          <div v-if="errorEntries.length" class="flex flex-col gap-2">
            <div
              v-for="e in errorEntries"
              :key="e.code"
              class="flex items-center justify-between p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30"
            >
              <div class="flex items-center gap-2">
                <span class="i-ri-alert-line size-4 text-red-600 dark:text-red-400" />
                <span class="font-mono text-xs text-red-700 dark:text-red-300">Código {{ e.code }}</span>
              </div>
              <span class="text-sm font-bold text-red-700 dark:text-red-300">{{ fmtNum(e.count) }}</span>
            </div>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            Nenhuma falha registrada.
          </div>
        </div>

        <!-- Timeline tab -->
        <div v-if="detailTab === 'timeline'" class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Atividade diária</h3>
          <div v-if="detail.daily_stats && detail.daily_stats.length" class="h-64">
            <Line :data="dailyChartData" :options="chartOptions" />
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            Sem dados diários ainda.
          </div>
        </div>

        <!-- Cost tab -->
        <div v-if="detailTab === 'cost'" class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-4">
          <div class="flex items-baseline gap-2">
            <h3 class="text-sm font-semibold text-n-slate-12">Total estimado:</h3>
            <span class="text-2xl font-extrabold text-n-slate-12">
              {{ fmtMoney(detail.cost.estimated_cost, detail.cost.currency) }}
            </span>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div
              v-for="(amount, category) in detail.cost.breakdown"
              :key="category"
              class="p-3 rounded-xl bg-n-slate-2 dark:bg-n-solid-2 flex items-center justify-between"
            >
              <div>
                <div class="text-xs text-n-slate-11 capitalize">{{ category }}</div>
                <div class="text-xs text-n-slate-11">
                  {{ fmtNum(detail.cost.billable_counts[category]) }} cobráveis
                </div>
              </div>
              <div class="text-sm font-bold text-n-slate-12">
                {{ fmtMoney(amount, detail.cost.currency) }}
              </div>
            </div>
          </div>
          <div class="text-xs text-n-slate-11 flex items-center gap-1 pt-2 border-t border-n-weak">
            <span class="i-ri-information-line size-3.5" />
            Valores estimados baseados em tarifas configuradas por conta. Meta não fornece o valor em moeda via webhook.
          </div>
        </div>

      </template>
    </template>
  </div>
</template>
